import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BustSignal from "./bustSignal";
import CaseArtwork from "./caseArtwork";
import TransmissionSignal from "./transmissionSignal";
import CurlCommand from "./curlCommand";
import PersonalFile from "./personalFile";
import { TerminalContent } from "./terminal";
import { contacts, profile, publicSignals, selectedExperience, stack } from "../content/portfolio";
import "../styles/home.css";
import "../styles/portfolioRefinement.css";

const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const portraitTriggerRef = useRef(null);
  const terminalTriggerRef = useRef(null);
  const portraitDialogRef = useRef(null);
  const terminalDialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const wasOverlayOpen = useRef(false);
  const isPortraitOpen = location.pathname === "/about";
  const [isTerminalOpen, setIsTerminalOpen] = useState(location.pathname === "/terminal");
  const isOverlayOpen = isPortraitOpen || isTerminalOpen;

  useEffect(() => {
    setIsTerminalOpen(location.pathname === "/terminal");
  }, [location.pathname]);

  const openTerminal = useCallback((event) => {
    returnFocusRef.current = event.currentTarget;
    if (location.pathname === "/about") navigate("/", {replace:true});
    setIsTerminalOpen(true);
  }, [location.pathname, navigate]);

  const closeTerminal = useCallback(() => {
    setIsTerminalOpen(false);
    if (location.pathname === "/terminal") navigate("/", {replace:true});
  }, [location.pathname, navigate]);

  const openPortrait = useCallback((event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    returnFocusRef.current = event.currentTarget;
    setIsTerminalOpen(false);
    navigate("/about");
  }, [navigate]);

  const closePortrait = useCallback(() => navigate("/", {replace:true}), [navigate]);

  useEffect(() => {
    if (isOverlayOpen && !returnFocusRef.current) {
      returnFocusRef.current = isPortraitOpen ? portraitTriggerRef.current : terminalTriggerRef.current;
    }
    if (isPortraitOpen) {
      portraitDialogRef.current?.querySelector("button")?.focus();
    }
    if (isTerminalOpen && !terminalDialogRef.current?.contains(document.activeElement)) {
      terminalDialogRef.current?.querySelector("button")?.focus({preventScroll:true});
    }
    if (wasOverlayOpen.current && !isOverlayOpen) {
      (returnFocusRef.current || portraitTriggerRef.current || terminalTriggerRef.current)?.focus();
    }
    wasOverlayOpen.current = isOverlayOpen;
  }, [isOverlayOpen, isPortraitOpen, isTerminalOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const dialog = isPortraitOpen ? portraitDialogRef.current : isTerminalOpen ? terminalDialogRef.current : null;
      if (event.key === "Tab" && dialog) {
        const items = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
        const first = items[0], last = items[items.length - 1];
        if (!first || !last) { event.preventDefault(); dialog.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
      if (event.key === "Escape") {
        if (isPortraitOpen) closePortrait();
        else if (isTerminalOpen) closeTerminal();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closePortrait, closeTerminal, isPortraitOpen, isTerminalOpen]);

  useEffect(() => {
    document.body.classList.toggle("terminal-open", isTerminalOpen);
    document.body.classList.toggle("overlay-open", isOverlayOpen);
    return () => {
      document.body.classList.remove("terminal-open");
      document.body.classList.remove("overlay-open");
    };
  }, [isOverlayOpen, isTerminalOpen]);

  return (
    <div className="dossier-page" id="top">
      <div className="dossier-surface" inert={isOverlayOpen} aria-hidden={isOverlayOpen ? true : undefined}>
        <header className="dossier-header">
          <nav className="dossier-nav" aria-label="Primary navigation">
            <a href="#experience">Experience</a>
            <a href="#signals" aria-label="Public signals">
              <span className="dossier-header-label-full" aria-hidden="true">Public signals</span>
              <span className="dossier-header-label-compact" aria-hidden="true">Signals</span>
            </a>
            <a href="#contact">Contact</a>
          </nav>
          <button type="button" className="dossier-terminal-trigger" aria-label="Open terminal" onClick={openTerminal} ref={terminalTriggerRef}>
            <span className="dossier-header-label-full" aria-hidden="true">Open terminal</span>
            <span className="dossier-header-label-compact" aria-hidden="true">Terminal</span>
          </button>
        </header>
        <main>
          <section className="dossier-hero" aria-labelledby="profile-title">
            <div className="dossier-hero-copy">
              <h1 className="dossier-name" id="profile-title" aria-label={profile.firstName + " " + profile.lastName}>
                <span className="dossier-name-filled" data-text={profile.firstName.toUpperCase()}>{profile.firstName.toUpperCase()}</span>
                <span className="dossier-name-outline" data-text={profile.lastName.toUpperCase()}>{profile.lastName.toUpperCase()}</span>
              </h1>
              <TransmissionSignal />
              <p className="dossier-headline">{profile.headline}</p>
              <p className="dossier-supporting">{profile.supporting}</p>
              <ul className="dossier-stack" aria-label="Primary technologies">{stack.map(technology => <li key={technology}>{technology}</li>)}</ul>
            </div>
            <BustSignal onOpen={openPortrait} triggerRef={portraitTriggerRef} />
          </section>
          <section className="dossier-section dossier-experience" id="experience" aria-labelledby="experience-title">
            <div className="dossier-section-heading">
              <p>Selected experience</p>
              <h2 id="experience-title">Production work, disclosed responsibly.</h2>
            </div>
            <div className="dossier-case-grid">
              {selectedExperience.map(experience => (
                <article className="dossier-case" aria-label={experience.title} key={experience.index}>
                  <div className="dossier-case-topline"><span>Case {experience.index}</span></div>
                  <CaseArtwork caseId={experience.index} />
                  <div className="dossier-case-copy">
                    <p>{experience.domain}</p><h3>{experience.title}</h3>
                    <p className="dossier-case-summary">{experience.summary}</p>
                    <ul aria-label={experience.title + " metrics"}>{experience.metrics.map(metric => <li key={metric}>{metric}</li>)}</ul>
                    <small>{experience.role.join(" · ")}</small>
                  </div>
                </article>
              ))}
            </div>
          </section>
          <section className="dossier-section dossier-signals" id="signals" aria-labelledby="signals-title">
            <div className="dossier-section-heading"><p>Public signals</p><h2 id="signals-title">Verifiable trail.</h2>
            </div>
            <div className="dossier-signal-list">
              {publicSignals.map((signal,index) => (
                <a key={signal.href} className="dossier-signal" href={signal.href} target="_blank" rel="noreferrer" aria-label={signal.label}>
                  <span>{String(index+1).padStart(2,"0")}</span>
                  <span><strong>{signal.label}</strong><small>{signal.detail}</small></span>
                  <span>{signal.kind}</span><span aria-hidden="true">↗</span>
                </a>
              ))}
            </div>
          </section>
        </main>
        <footer className="dossier-footer" id="contact">
          <div><p>Let's talk about what you're building.</p><span>Email is a good place to start.</span></div>
          <nav aria-label="Contact links">{contacts.map(contact => (
            <a key={contact.label} href={contact.href} target={contact.external ? "_blank" : undefined} rel={contact.external ? "noreferrer" : undefined} aria-label={contact.label}>
              <span>{contact.label}</span><strong>{contact.value}</strong>
            </a>
          ))}</nav>
          <div className="dossier-curl-hint"><CurlCommand /></div>
        </footer>
      </div>
      {isPortraitOpen && <PersonalFile dialogRef={portraitDialogRef} onClose={closePortrait} />}
      {isTerminalOpen && (
        <div className="terminal-modal-backdrop" role="presentation" onClick={event => {if (event.target === event.currentTarget) closeTerminal();}}>
          <div className="terminal-modal" role="dialog" aria-modal="true" aria-label="Interactive terminal" ref={terminalDialogRef} tabIndex={-1}>
            <TerminalContent onClose={closeTerminal} />
          </div>
        </div>
      )}
    </div>
  );
};
export default Home;
